import React, { useCallback, useEffect, useState } from 'react';

import {
  BulletEmptyIcon,
  DisconnectedIcon,
  LinkIcon,
  LoopIcon,
} from '@lp/ds-next';
import { Alert, Col, Flex, Row, Tag, Typography, theme } from 'antd';
import { useParams } from 'react-router-dom';

import { CardCommand } from './CardCommand';
import useRemoteControl from './hook/useRemoteControl';
import { Loader } from '../../../../components/loaders/Loader';
import { IShipment } from '../../../shipments/models/shipment';
import useGetBox from '../../hooks/useGetBox';

export interface RemoteError {
  title: string;
  message: string;
}

export interface IBoxConnectedState {
  is_connected: boolean;
  is_direct_mode: boolean;
}

const DURING_MAX = 120; // 2 minutes in seconds
const INTERVAL = 2000; // 2 seconds in milliseconds

// Object with action card content
const cardRemoteContent = [
  {
    title: 'Lock/Unlock',
    subtitle: 'Lock / Unlock the product remotely',
    name: 'Lock/Unlock',
    command: 'Trigger command',
    statusKey: 'Status',
    status: '-',
    action: 'COMMAND_TYPE_LOCK_UNLOCK',
    activated: true,
    requiresConfirmation: false,
    loader: (
      <Flex vertical style={{ marginBottom: '0.8rem' }}>
        <Loader />
        <Typography.Text>Locking/Unlocking in progress...</Typography.Text>
      </Flex>
    ),
  },
  {
    title: 'Received',
    subtitle: 'Be careful, this order is definitive',
    name: 'Confirm delivery',
    command: 'Trigger command',
    statusKey: 'Status',
    status: '-',
    action: 'COMMAND_TYPE_RECEIVED',
    activated: true,
    requiresConfirmation: false,
    loader: (
      <Flex vertical style={{ marginBottom: '0.8rem' }}>
        <Loader />
        <Typography.Text>
          Confirmation of delivery in progress...
        </Typography.Text>
      </Flex>
    ),
  },
  {
    title: 'Clean up',
    subtitle: 'Remove the ongoing shipment, and reset the product',
    name: 'Clean up',
    command: 'Trigger command',
    statusKey: 'Status',
    status: '-',
    action: 'COMMAND_TYPE_CLEANUP',
    activated: true,
    requiresConfirmation: true,
    confirmationMessage:
      'Are you sure you want to clean this product data? This action cannot be undone.',
    loader: (
      <Flex vertical style={{ marginBottom: '0.8rem' }}>
        <Loader />
        <Typography.Text>Cleanup in progress...</Typography.Text>
      </Flex>
    ),
    loaderShipment: (
      <Flex vertical style={{ marginBottom: '0.8rem' }}>
        <Loader />
        <Typography.Text>Updating shipment status...</Typography.Text>
      </Flex>
    ),
  },
];

export const RemonteControlPanel = ({
  isOpen,
  shipment,
  onCleanupComplete,
}: {
  isOpen: boolean;
  shipment?: IShipment;
  onCleanupComplete?: () => void;
}) => {
  const [startTime, setStartTime] = useState(Date.now() / 1000);
  const [isFirstOpenning, setIsFirstOpenning] = useState<boolean>(true);
  const [boxConnected, setBoxConnected] = useState<IBoxConnectedState>();
  const [loader, setLoader] = useState<boolean>(false);
  const [showFinalNotice, setShowFinalNotice] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<RemoteError>({
    title: '',
    message: '',
  });
  const [warning, setWarning] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<RemoteError>({
    title: '',
    message: '',
  });

  const isConnected = boxConnected?.is_connected === true;
  const isDirectMode = boxConnected?.is_direct_mode === true;

  const { id } = useParams<{ id: string }>();
  const { box } = useGetBox(id);
  const { token } = theme.useToken();

  const { getConnectivity } = useRemoteControl(id);

  // Call for check if direct mode is activated
  const connection = useCallback(async () => {
    const data = await getConnectivity();
    if (data && data.is_connected && data.is_direct_mode) {
      setError(false);
      setIsFirstOpenning(false);
    }

    setBoxConnected(data);
  }, [getConnectivity, setIsFirstOpenning]);

  // Starts direct check active mode for 2min
  useEffect(() => {
    const connectInterval = setInterval(() => {
      if (isOpen) {
        const isWithinConnectionWindow =
          Date.now() / 1000 - startTime < DURING_MAX;
        if (!boxConnected && isWithinConnectionWindow) {
          connection();
          setLoader(true);
        } else {
          clearInterval(connectInterval);
          if (!boxConnected) {
            setError(true);
            setLoader(false);
          }
        }
      } else {
        clearInterval(connectInterval);
      }
    }, INTERVAL);

    return () => clearInterval(connectInterval);
  }, [boxConnected, startTime, connection, isOpen]);

  useEffect(() => {
    if (
      (isConnected && !isDirectMode) ||
      (!isConnected && !isDirectMode && !isFirstOpenning)
    ) {
      setErrorMessage({
        title: 'Connection is lost',
        message: 'Please refresh to resolve this or try later.',
      });
      setError(true);
    } else if (!isConnected && !isDirectMode && isFirstOpenning) {
      setErrorMessage({
        title: 'Connection impossible',
        message: 'If problem persist contact IT support.',
      });
    }
  }, [isConnected, isDirectMode, isFirstOpenning]);

  // Is called by the refresh button to restart the connection info request
  const forceRefresh = () => {
    connection();
    setStartTime(Date.now() / 1000);
  };

  return (
    <Row
      gutter={[0, 32]}
      style={{ padding: '1.5rem', maxWidth: '56.375rem' }}
      data-testid="modal-remote-control"
    >
      <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Remote control
        </Typography.Title>
      </Col>
      <Col span={24}>
        <Flex
          justify="space-between"
          align="center"
          style={{
            backgroundColor: token.colorBgLayout,
            opacity: showFinalNotice ? 0.3 : 1,
            pointerEvents: showFinalNotice ? 'none' : 'initial',
            borderRadius: '1.25rem',
            padding: '1.25rem',
          }}
        >
          <Flex align="center">
            <Typography.Text strong style={{ fontSize: '1.125rem' }}>
              {box.productDescription}
            </Typography.Text>
          </Flex>
          <Flex gap="1.5rem" align="center">
            <Flex
              onClick={() => {
                forceRefresh();
              }}
              data-testid="loop-refresh"
            >
              <LoopIcon
                style={{
                  width: '1.3rem',
                  height: '1.3rem',
                  cursor: loader && !boxConnected ? 'initial' : 'pointer',
                  color:
                    loader && !boxConnected
                      ? token.colorTextDisabled
                      : token.colorPrimary,
                }}
              />
            </Flex>
            <Tag
              color={isDirectMode ? 'green' : 'red'}
              data-testid="direct-mode-info"
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  verticalAlign: 'middle',
                }}
              >
                <BulletEmptyIcon
                  style={{ width: '.75rem', height: '.75rem' }}
                />
                Direct mode
              </span>
            </Tag>
            <Tag
              color={isConnected ? 'green' : 'default'}
              data-testid="connected-disconnected-info"
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  verticalAlign: 'middle',
                }}
              >
                {isConnected ? <LinkIcon /> : <DisconnectedIcon />}
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </Tag>
            {/* <Chip
              state={locked ? 'default' : 'idle'}
              type="tags"
              variant="titleS"
              label={locked ? 'Locked' : 'Unlocked'}
              icon={locked ? <LockOnIcon /> : <LockOffIcon />}
            /> */}
          </Flex>
        </Flex>
      </Col>

      {error && (
        <Col span={24}>
          <Alert
            type="error"
            data-testid="toat-error-message"
            message={
              <>
                <Typography.Text strong style={{ color: token.colorError }}>
                  {errorMessage.title}
                </Typography.Text>
                <br />
                <Typography.Text style={{ color: token.colorError }}>
                  {errorMessage.message}
                </Typography.Text>
              </>
            }
          />
        </Col>
      )}
      {warning && (
        <Col span={24}>
          <Alert
            type="warning"
            data-testid="warning-message"
            message={
              <>
                <Typography.Text strong style={{ color: token.colorWarning }}>
                  {warningMessage.title}
                </Typography.Text>
                <br />
                <Typography.Text style={{ color: token.colorWarning }}>
                  {warningMessage.message}
                </Typography.Text>
              </>
            }
          />
        </Col>
      )}

      <Col span={24}>
        <Flex
          gap="2.75rem"
          wrap="wrap"
          style={{
            opacity: !boxConnected || showFinalNotice ? 0.3 : 1,
            pointerEvents: showFinalNotice ? 'none' : 'initial',
          }}
          justify="flex-start"
          data-testid="opacity-final-notice"
        >
          {cardRemoteContent.map((cardContent) => (
            <div key={cardContent.action}>
              <CardCommand
                cardContent={cardContent}
                isConnected={isConnected}
                isDirectMode={isDirectMode}
                setShowFinalNotice={setShowFinalNotice}
                shipmentId={shipment?.id}
                disabled={
                  cardContent.action === 'COMMAND_TYPE_RECEIVED' &&
                  !shipment?.id
                }
                setErrorMessage={setErrorMessage}
                setError={setError}
                setWarning={setWarning}
                setWarningMessage={setWarningMessage}
                onCleanupComplete={onCleanupComplete}
              />
            </div>
          ))}
        </Flex>
      </Col>
      {showFinalNotice && (
        <Flex
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Typography.Text
            strong
            style={{ fontSize: '1.125rem' }}
            data-testid="end-process-message"
          >
            Delivery process successfully completed
          </Typography.Text>
        </Flex>
      )}
      {loader && !boxConnected && (
        <Flex
          vertical
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Loader />
          <Typography.Text strong data-testid="connection-loader">
            Connection in progress...
          </Typography.Text>
        </Flex>
      )}
    </Row>
  );
};
